/**
 * IdleTimeoutManager – activity-aware session timeout.
 *
 * • Tracks real user activity (mouse, keyboard, scroll, click, touch).
 * • Silently refreshes the JWT every REFRESH_INTERVAL while the user is active.
 * • After IDLE_TIMEOUT of true inactivity shows a warning modal with a countdown.
 * • If the user interacts during the warning the session is extended seamlessly.
 * • If the countdown expires the user is logged out.
 */
import React, { useEffect, useRef, useCallback, useState } from 'react';
import { Modal, Typography, Button } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';

const { Text } = Typography;

// ── Tunables ────────────────────────────────────────────────────────────────
const IDLE_TIMEOUT_MS      = 20 * 60 * 1000;  // 20 minutes of no activity → show warning
const WARNING_DURATION_MS  = 2 * 60 * 1000;   // 2-minute countdown before forced logout
const REFRESH_INTERVAL_MS  = 15 * 60 * 1000;  // silently refresh token every 15 min while active
const ACTIVITY_THROTTLE_MS = 5 * 1000;         // throttle activity event handling (perf)

// Events that count as "user is active"
const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
  'mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click',
  'wheel', 'pointerdown',
];

interface IdleTimeoutManagerProps {
  children: React.ReactNode;
}

const IdleTimeoutManager: React.FC<IdleTimeoutManagerProps> = ({ children }) => {
  const { isAuthenticated, logout, refreshToken } = useAuth();

  // ── Refs for timers ────────────────────────────────────────────────────
  const idleTimerRef    = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownRef    = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const throttledRef    = useRef<number>(0);

  // ── Refs that mirror the latest callback / state values ────────────────
  // This avoids stale-closure bugs: event listeners are bound once,
  // but always read the *current* values through these refs.
  const showWarningRef  = useRef(false);
  const logoutRef       = useRef(logout);
  const refreshTokenRef = useRef(refreshToken);

  // UI state (for rendering only)
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.floor(WARNING_DURATION_MS / 1000));

  // Keep refs in sync with latest values
  useEffect(() => { logoutRef.current = logout; }, [logout]);
  useEffect(() => { refreshTokenRef.current = refreshToken; }, [refreshToken]);
  useEffect(() => { showWarningRef.current = showWarning; }, [showWarning]);

  // ── Helpers (stable – never change identity) ───────────────────────────

  const clearAllTimers = useCallback(() => {
    if (idleTimerRef.current)    { clearTimeout(idleTimerRef.current);    idleTimerRef.current = null; }
    if (warningTimerRef.current) { clearTimeout(warningTimerRef.current); warningTimerRef.current = null; }
    if (refreshTimerRef.current) { clearInterval(refreshTimerRef.current); refreshTimerRef.current = null; }
    if (countdownRef.current)    { clearInterval(countdownRef.current);   countdownRef.current = null; }
  }, []);

  /** Called when the warning countdown expires with no response. */
  const handleForceLogout = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);
    logoutRef.current();
  }, [clearAllTimers]);

  /** Show the "Are you still there?" warning + start countdown. */
  const startWarning = useCallback(() => {
    // Guard: don't start a second warning if one is already showing
    if (showWarningRef.current) return;

    setSecondsLeft(Math.floor(WARNING_DURATION_MS / 1000));
    setShowWarning(true);

    // Countdown ticker – update secondsLeft every second
    countdownRef.current = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) {
          handleForceLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Safety-net hard timer in case the interval drifts
    warningTimerRef.current = setTimeout(handleForceLogout, WARNING_DURATION_MS);
  }, [handleForceLogout]);

  /** Reset the idle clock – called on every real user activity. */
  const resetIdleTimer = useCallback(() => {
    lastActivityRef.current = Date.now();

    // If the warning is currently visible, dismiss it and extend the session
    if (showWarningRef.current) {
      setShowWarning(false);
      if (warningTimerRef.current) { clearTimeout(warningTimerRef.current); warningTimerRef.current = null; }
      if (countdownRef.current)    { clearInterval(countdownRef.current);   countdownRef.current = null; }
      // Refresh the token immediately when user comes back from idle warning
      refreshTokenRef.current();
    }

    // (Re)start the idle timeout
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(startWarning, IDLE_TIMEOUT_MS);
  }, [startWarning]);

  // ── User-clicks "Stay Logged In" button explicitly ─────────────────────
  const handleStayLoggedIn = useCallback(() => {
    resetIdleTimer();
  }, [resetIdleTimer]);

  // ── Activity event handler (throttled) ──────────────────────────────────
  const onActivity = useCallback(() => {
    const now = Date.now();
    if (now - throttledRef.current < ACTIVITY_THROTTLE_MS) return;
    throttledRef.current = now;
    resetIdleTimer();
  }, [resetIdleTimer]);

  // ── Setup / teardown ────────────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated) {
      clearAllTimers();
      setShowWarning(false);
      return;
    }

    // Bind activity listeners
    ACTIVITY_EVENTS.forEach(evt => document.addEventListener(evt, onActivity, { passive: true }));

    // Start the idle timer
    idleTimerRef.current = setTimeout(startWarning, IDLE_TIMEOUT_MS);

    // Start the silent token-refresh interval
    refreshTimerRef.current = setInterval(() => {
      // Only refresh if user has been active recently
      if (Date.now() - lastActivityRef.current < IDLE_TIMEOUT_MS) {
        refreshTokenRef.current();
      }
    }, REFRESH_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach(evt => document.removeEventListener(evt, onActivity));
      clearAllTimers();
    };
  }, [isAuthenticated, onActivity, startWarning, clearAllTimers]);

  // ── Render ──────────────────────────────────────────────────────────────
  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;

  return (
    <>
      {children}
      <Modal
        open={showWarning}
        title={null}
        footer={null}
        closable={false}
        maskClosable={false}
        centered
        width={420}
        styles={{ body: { textAlign: 'center', padding: '32px 24px' } }}
      >
        <ClockCircleOutlined style={{ fontSize: 48, color: '#faad14', marginBottom: 16 }} />
        <div style={{ marginBottom: 8 }}>
          <Text strong style={{ fontSize: 18 }}>Session Expiring</Text>
        </div>
        <div style={{ marginBottom: 20 }}>
          <Text type="secondary">
            You've been inactive for a while. For security, you'll be logged out in
          </Text>
        </div>
        <div style={{ marginBottom: 24 }}>
          <Text strong style={{ fontSize: 32, fontFamily: 'monospace', color: secondsLeft <= 30 ? '#ff4d4f' : '#faad14' }}>
            {minutes}:{seconds.toString().padStart(2, '0')}
          </Text>
        </div>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
          <Button type="primary" size="large" onClick={handleStayLoggedIn}>
            Stay Logged In
          </Button>
          <Button size="large" danger onClick={handleForceLogout}>
            Logout Now
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default IdleTimeoutManager;
